import { Tracer } from '@opentelemetry/api';
import { ILogger } from 'app/tracing/logging';
import { XMLParser } from 'fast-xml-parser';

import { WMTSTileSet, WMTSUtils } from './WMTSUtils';
import { Capabilities } from './WMTSTypes';

export class WMTSTileSetFetcher {
    private logger: ILogger;
    private tracer: Tracer;
    private wmtsUtils: WMTSUtils;

    public constructor(logger: ILogger, tracer: Tracer, wmtsUtils: WMTSUtils) {
        this.logger = logger;
        this.tracer = tracer;
        this.wmtsUtils = wmtsUtils;
    }

    private async fetchWMTSCapabilitiesAsync() {
        const span = this.tracer.startSpan('fetchWMTSCapabilitiesAsync');

        const capabilitiesURL = import.meta.env.VITE_WMTS_CAPABILITIES_ENDPOINT as string;
        const response = await fetch(capabilitiesURL);
        if (!response.ok) {
            this.logger.error('Failed to retrieve WMTS capabilities', response);
        }

        const xml = await response.text();
        const parser = new XMLParser({ ignoreAttributes: false });
        const wmtsCapabilities = parser.parse(xml)['Capabilities'] as Capabilities;
        if (!wmtsCapabilities) {
            throw new Error('Failed to parse WMTS capabilities');
        }

        span.end();

        return wmtsCapabilities;
    }

    public async getWMTSTileSetAsync() {
        const wmtsCapabilities = await this.fetchWMTSCapabilitiesAsync();

        // NOTE(bjarne): Load defaults.
        const getTile = this.wmtsUtils.getPossibleRequests(wmtsCapabilities, 'GetTile', 'KVP')[0];
        const getTileURL = getTile ? getTile['@_xlink:href'] : undefined;
        if (!getTileURL) {
            this.logger.error("Couldn't find a GetTile URL that has a KVP constraint value type.", wmtsCapabilities);
        }

        // TODO(bjarne): Select a good default once we have access to the geospatial server.
        const firstLayer = this.wmtsUtils.getFirst(wmtsCapabilities.Contents?.Layer);
        const layerID = firstLayer ? firstLayer['ows:Identifier'] : undefined;
        const firstStyle = this.wmtsUtils.getFirst(firstLayer?.Style);
        const styleID = firstStyle ? firstStyle['ows:Identifier'] : undefined;
        const firstFormat = this.wmtsUtils.getFirst(firstLayer?.Format);
        const formatID = firstFormat;
        const firstMatrixSetLink =
            this.wmtsUtils.find(firstLayer?.TileMatrixSetLink, x =>
                // NOTE(bjarne): We'd like to use the GoogleMapsCompatible map.
                x.TileMatrixSet.toLowerCase().includes('google'),
            ) ?? this.wmtsUtils.getFirst(firstLayer?.TileMatrixSetLink);
        const tileMatrixSetID = firstMatrixSetLink ? firstMatrixSetLink.TileMatrixSet : undefined;
        const tileMatrixSet = this.wmtsUtils.find(
            wmtsCapabilities.Contents?.TileMatrixSet,
            x => x['ows:Identifier'].toString() === tileMatrixSetID?.toString(),
        );

        const wmtsTileSet: WMTSTileSet = {
            getTileURL: getTileURL,
            layerID: layerID,
            styleID: styleID,
            formatID: formatID,
            tileMatrixSetID: tileMatrixSetID,
            tileMatrixSet: tileMatrixSet,
        };

        return wmtsTileSet;
    }
}
